import { Button, DialogContent, IconButton, Typography } from '@material-ui/core';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useStyles, StyledDialog } from './MissingInfoDialog.styles';
import { talentService } from 'shared/services/talentService';
import { ls } from 'shared/utils/ls';
import { useAppSelector } from 'shared/redux/store';
import { ISpecificTalentResponsePayload } from 'shared/interfaces/ITalent';
import NewImage from '../NewImage';
import StepOne from './Steps/StepOne';
import StepTwo from './Steps/StepTwo';
import { useQueryClient } from 'react-query';
import { HiOutlineArrowRight } from 'react-icons/hi';
import { mediaService } from 'shared/services/mediaService';
import { CheckBox, CloseIcon, LoadingSpinnerIconTwoTone } from '@room-match/shared-ui-components';

const { getMediaList } = mediaService();
const { getMyData, getResume, updateTalent, updateMe, getSkills } = talentService();

interface IMissing {
  missing: 'missingData' | 'missingMedia';
}

const MissingInfoDialog: React.FC = () => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [doNotShow, setDoNotShow] = useState<boolean>(false);
  const [stepOneOnly, setStepOneOnly] = useState<boolean>(false);
  const [talent, setTalent] = useState<ISpecificTalentResponsePayload['data']['talent']>();
  const [steps, setSteps] = useState<IMissing['missing'][]>([]);
  const [step, setStep] = useState<1 | 2>(1);
  const [hasMissingData, setHasMissingData] = useState<boolean>(false);
  const [hasMissingMedia, setHasMissingMedia] = useState<boolean>(false);
  const [birth, setBirth] = useState({
    day: '',
    month: '',
    year: '',
  });

  const [skillsCount, setSkillsCount] = useState<number>(0);
  const [creditsCount, setCreditsCount] = useState<number>(0);
  const [mediaLength, setMediaLength] = useState<{
    image: number;
    video: number;
    audio: number;
  }>({
    image: 0,
    video: 0,
    audio: 0,
  });
  const [mediamessage, setMediamessage] = useState<string[]>([]);
  const { data: mediaData, isLoading: mediaLoading } = getMediaList({ media_bin: true });
  const { data: talentResumeData, isLoading: resumeLoading } = getResume();
  const { data: skillData, isLoading: skillsLoading } = getSkills();

  const { mutateAsync: talentUpdate, isLoading: isTalentSaving } = updateTalent();
  const { mutateAsync: meUpdate, isLoading: isMeSaving } = updateMe();
  const queryClient = useQueryClient();

  const { getLS, setLS } = ls();
  const user = JSON.parse(getLS('user') || '{}');
  const { user: userState } = useAppSelector((state) => state);
  const userData = userState?.user || user;

  const { data: myData, isLoading: myDataLoading } = getMyData(userData.id);
  const sData = myData?.data?.talent;
  const [metricValue, setMetricValue] = useState(
    sData?.statistics && sData?.statistics?.metric_system ? sData?.statistics?.metric_system : 'AL',
  );

  const country = user?.data?.attributes?.other_phone_numbers?.filter((num: any) => num.type === 'primary');
  const userCountry = country && country.length > 0 ? country[0].country_code.toLowerCase() : 'au';

  const adultMinor = userData?.adult_minor || sData?.adult_minor;
  const isMinor = adultMinor?.toLowerCase() !== 'adult';
  const [ageRangeVal, setAgeRangeVal] = useState<number[]>([15, 23]);

  useEffect(() => {
    if (sData?.statistics?.metric_system) {
      if (sData?.statistics?.metric_system === 'Imperial/Metric') setMetricValue('AL');
      else if (sData?.statistics?.metric_system === 'Imperial') setMetricValue('IN');
      else setMetricValue('CM');
    } else {
      setMetricValue('AL');
    }
  }, [sData?.statistics]);

  useEffect(() => {
    if (!myDataLoading && myData && myData.data) {
      const { talent: talentData } = myData.data;
      setTalent(talentData);

      const birthdate = userState?.user?.attributes?.birth_date;
      if (birthdate) {
        const birthSplit = birthdate.split('/');
        setBirth({
          day: parseInt(birthSplit[0]) > 9 ? birthSplit[0] : birthSplit[0].substring(1),
          month: birthSplit[1],
          year: birthSplit[2],
        });
      }

      const genderMissing = !talentData.gender || talentData.gender === '';
      const ethnicityMissing =
        !talentData?.general?.ethnic_appearance_primary || talentData?.general?.ethnic_appearance_primary === '';
      const heightMissing = !talentData?.statistics?.height || talentData?.statistics?.height === '';
      const weightMissing =
        userCountry === 'us' && (!talentData?.statistics?.weight || talentData?.statistics?.weight === '');
      const nationalityMissing = !talentData?.general?.nationality;
      const talentAgeFrom = userData?.attributes?.age_range_from || user?.data?.attributes?.age_range_from;
      const talentAgeTo = userData?.attributes?.age_range_to || user?.data?.attributes?.age_range_to;
      setAgeRangeVal([talentAgeFrom, talentAgeTo]);

      const missingUnion = userCountry === 'us' && !(talentData.unions.length > 0 || talentData.unions[0]);
      const hairColorMissing = !talentData?.general?.hair_colour || talentData?.general?.hair_colour === '';
      const eyeColorMissing = !talentData?.general?.eye_colour || talentData?.general?.eye_colour === '';

      if (
        genderMissing ||
        missingUnion ||
        ethnicityMissing ||
        nationalityMissing ||
        heightMissing ||
        weightMissing ||
        hairColorMissing ||
        eyeColorMissing
      ) {
        setHasMissingData(true);
        setSteps((prevstate) => [...prevstate, 'missingData']);
      }
    }

    let skillCount = 0;
    let creditCount = 0;

    if (skillData) {
      skillData?.data?.attributes?.skills?.map((skill: any) =>
        skill.groups?.map((g: any) =>
          g.skills.forEach((s: any) => {
            if (s.is_selected) skillCount++;
          }),
        ),
      );
      setSkillsCount(skillCount);
    }

    if (talentResumeData) {
      const resume = talentResumeData?.data?.attributes?.resume;
      creditCount = resume?.categories?.reduce((a, b) => a + b.credits.length, 0) || 0;
      setCreditsCount(creditCount);
    }

    if (mediaData) {
      let mm = [];
      const imgs = (mediaData?.data || []).filter((f) => f.attributes.file_type === 'image').length;
      const vids = (mediaData?.data || []).filter((f) => f.attributes.file_type === 'video').length;
      const auds = (mediaData?.data || []).filter((f) => f.attributes.file_type === 'audio').length;

      setMediaLength({
        image: imgs,
        video: vids,
        audio: auds,
      });

      if (imgs <= 3) {
        mm.push(`${imgs} Image`);
      }
      if (vids <= 3) {
        mm.push(`${vids} Video`);
      }
      if (auds <= 3) {
        mm.push(`${auds} Audio`);
      }

      setMediamessage(mm);
      const missingMedia = imgs <= 3 || vids <= 3 || auds <= 3 || skillCount <= 3 || creditCount <= 3;
      setHasMissingMedia(missingMedia);
      if (missingMedia) {
        setSteps((prevstate) => [...prevstate, 'missingMedia']);
      }
    }
  }, [myData, myDataLoading, resumeLoading, mediaLoading, skillsLoading]);

  const filledUpData = useMemo(() => {
    const genderMissing = !talent?.gender || talent?.gender === '';
    const ethnicityMissing =
      !talent?.general?.ethnic_appearance_primary || talent?.general?.ethnic_appearance_primary === '';
    const heightMissing = !talent?.statistics?.height || talent?.statistics?.height === '';
    const weightMissing = userCountry === 'us' && (!talent?.statistics?.weight || talent?.statistics?.weight === '');
    const nationalityMissing = !talent?.general?.nationality;

    const missingUnion = userCountry === 'us' && !((talent?.unions || []).length > 0 || talent?.unions[0]);
    const hairColorMissing = !talent?.general?.hair_colour || talent?.general?.hair_colour === '';
    const eyeColorMissing = !talent?.general?.eye_colour || talent?.general?.eye_colour === '';

    const withMissingData =
      genderMissing ||
      missingUnion ||
      ethnicityMissing ||
      nationalityMissing ||
      heightMissing ||
      weightMissing ||
      hairColorMissing ||
      eyeColorMissing;

    return !withMissingData;
  }, [talent]);

  useEffect(() => {
    const doNotShowMissing = JSON.parse(getLS('do_not_show_missing_info') || false);
    if (!doNotShowMissing) {
      if ((hasMissingData && hasMissingMedia) || (!hasMissingData && hasMissingMedia)) {
        setOpen(true);
        setStepOneOnly(false);
      } else if (hasMissingData && !hasMissingMedia) {
        setStepOneOnly(true);
        setOpen(true);
      } else if (!hasMissingData && !hasMissingMedia) {
        setStepOneOnly(false);
        setOpen(false);
      }
    }
  }, [hasMissingData, hasMissingMedia]);

  const handleCloseModal = () => {
    if (doNotShow) {
      setLS('do_not_show_missing_info', JSON.stringify(true));
    }
    setOpen(false);
  };

  useEffect(() => {
    if (steps.includes('missingMedia') && !steps.includes('missingData')) {
      setStep(2);
    } else {
      setStep(1);
    }
  }, [steps]);

  const handleSave = async () => {
    if (step === 1) {
      const talentData = { ...talent, age_range_from: ageRangeVal[0], age_range_to: ageRangeVal[1] };
      const meData = {
        age_range_from: ageRangeVal[0],
        age_range_to: ageRangeVal[1],
      };
      await talentUpdate(
        {
          ...talentData,
        },
        {
          onSuccess: async () => {
            await queryClient.refetchQueries(['talents/general']);
            await queryClient.refetchQueries(['talents/statistics']);
            await queryClient.refetchQueries(['my_data']);
          },
        },
      );
      await meUpdate(
        {
          ...meData,
        },
        {
          onSuccess: async () => await queryClient.refetchQueries(['me']),
        },
      );

      if (stepOneOnly) {
        setOpen(false);
      } else {
        setStep(2);
      }
    } else {
      handleCloseModal();
    }
  };

  const firstName = talent?.stage_first_name ?? talent?.first_name;

  if (mediaLoading || resumeLoading) {
    return (
      <div className={`${classes.missingInfos} loader`}>
        <LoadingSpinnerIconTwoTone style={{ width: 100, height: 100 }} />
      </div>
    );
  }
  if (myDataLoading || resumeLoading || mediaLoading || skillsLoading) return null;

  return (
    <StyledDialog classes={{ paper: classes.paper }} open={open} maxWidth="md" fullWidth>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.contentHeadCont}>
          {step === 2 && (
            <IconButton className={classes.closeButton} onClick={() => handleCloseModal()}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )}
          <div>
            <NewImage src={userData?.attributes?.picture} forDialog />
          </div>
          <div>
            <Typography className={classes.helloText}>
              Hello, {firstName}
              <br />
              You're missing vital profile information.
            </Typography>
            {step === 1 && (
              <Typography className={classes.hiddenMobile}>
                When your Agent Submits you for a project and a Casting Director considers you for an audition, they
                filter submissions by critical information that your casting.com profile is missing.
              </Typography>
            )}
          </div>
          <div className={classes.mobileDiv}>
            {step === 1 && (
              <Typography>
                When your Agent Submits you for a project and a Casting Director considers you for an audition, they
                filter submissions by critical information that your casting.com profile is missing.
              </Typography>
            )}
          </div>
        </div>
        {step === 1 && (
          <StepOne
            talent={talent!}
            setTalent={setTalent as Dispatch<SetStateAction<ISpecificTalentResponsePayload['data']['talent']>>}
            isMinor={isMinor}
            metricValue={metricValue!}
            ageRangeVal={ageRangeVal}
            setAgeRangeVal={setAgeRangeVal}
            country={userCountry}
            birth={birth}
          />
        )}
        {!stepOneOnly && step === 2 && (
          <StepTwo
            mediaLength={mediaLength}
            skillsCount={skillsCount}
            creditsCount={creditsCount}
            mediamessage={mediamessage}
          />
        )}
        <div className={classes.buttonContainer}>
          {step === 2 && (
            <div className={classes.doNotShowAgain}>
              <CheckBox
                checked={doNotShow}
                value={doNotShow}
                onChange={(e) => setDoNotShow(e.target.checked)}
                name="do_not_show"
              />
              <Typography>Do not show me again</Typography>
            </div>
          )}
          <Button
            type="button"
            variant="contained"
            color="primary"
            disableElevation
            disabled={(step === 1 && !filledUpData) || isTalentSaving || isMeSaving}
            className={classes.updateBtn}
            onClick={() => handleSave()}
            style={{ marginLeft: 'auto' }}
          >
            {step === 1 && 'Save and'} {step === 1 && !stepOneOnly ? 'Next' : 'Close'}{' '}
            {step === 1 && <HiOutlineArrowRight style={{ marginLeft: 10, width: 20, height: 20 }} />}
          </Button>
        </div>
      </DialogContent>
    </StyledDialog>
  );
};

export default MissingInfoDialog;
